import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { PackageCard } from '../components/PackageCard'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { listPackages } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import { usePageMeta } from '../hooks/usePageMeta'
import { pageMeta } from '../seo/pageMeta'
import type { Package } from '../types/packages'

const LANGUAGES = ['', 'go', 'python', 'typescript', 'javascript', 'rust', 'java', 'ruby', 'shell']
const SORT_OPTIONS = [
  { value: 'downloads', label: 'Most Downloaded' },
  { value: 'newest', label: 'Newest' },
  { value: 'name', label: 'Name A–Z' },
]
const PAGE_SIZE = 24

export default function Packages() {
  usePageMeta(pageMeta('/packages'))

  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const [packages, setPackages] = useState<Package[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const query = searchParams.get('q') || ''
  const language = searchParams.get('language') || ''
  const sort = searchParams.get('sort') || 'downloads'
  const page = parseInt(searchParams.get('page') || '1', 10)
  const officialOnly = searchParams.get('official') === 'true'

  const [searchInput, setSearchInput] = useState(query)
  const { isAuthenticated } = useAuth()

  const fetchPackages = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await listPackages({
        q: query || undefined,
        language: language || undefined,
        official: officialOnly || undefined,
        sort,
        page,
        limit: PAGE_SIZE,
      })
      setPackages(resp.packages ?? [])
      setTotal(resp.total)
    } catch {
      setError('Failed to load packages. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [query, language, sort, page, officialOnly])

  useEffect(() => {
    fetchPackages()
  }, [fetchPackages])

  // Sync searchInput when URL query changes (e.g. browser back/forward)
  useEffect(() => {
    setSearchInput(query)
  }, [query])

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }
    next.delete('page') // reset to page 1 on filter change
    setSearchParams(next)
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    updateParam('q', searchInput.trim())
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="min-h-screen">
      {!isAuthenticated && <PublicNavBar />}

      {/* Hero */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Package Registry
          </h1>
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">
              Discover and download packages built by the Factory Nexus community.
            </p>
            {isAuthenticated && (
              <button
                onClick={() => navigate('/packages/new')}
                className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-500 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Package
              </button>
            )}
          </div>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-xl">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search packages..."
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-500 transition-colors"
            >
              Search
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Language filter */}
          <select
            value={language}
            onChange={(e) => updateParam('language', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Languages</option>
            {LANGUAGES.filter(Boolean).map((l) => (
              <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={sort}
            onChange={(e) => updateParam('sort', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Official toggle */}
          <button
            onClick={() => updateParam('official', officialOnly ? '' : 'true')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
              officialOnly
                ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-500'
                : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-400'
            }`}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 12 12" fill="currentColor">
              <path d="M6 1l1.545 3.13 3.455.502-2.5 2.436.59 3.44L6 8.882 2.91 10.508l.59-3.44L1 4.632l3.455-.502L6 1z" />
            </svg>
            Official Only
          </button>

          <span className="ml-auto text-sm text-gray-400 dark:text-gray-500">
            {loading ? '' : `${total.toLocaleString()} package${total !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* Content */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-400 mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : packages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <svg className="w-12 h-12 text-gray-300 dark:text-gray-700 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400 font-medium">No packages found</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {query ? `No results for "${query}"` : 'Be the first to publish a package.'}
            </p>
            {query && (
              <button
                onClick={() => { setSearchInput(''); updateParam('q', '') }}
                className="mt-3 text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Card grid — side-by-side layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {packages.map((pkg) => (
                <PackageCard key={pkg.id} pkg={pkg} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => updateParam('page', String(page - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => updateParam('page', String(page + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* CTA footer — only shown to unauthenticated visitors */}
      {!isAuthenticated && (
        <div className="border-t border-gray-200 dark:border-gray-800 mt-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">Publish your own package</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Sign up and start sharing your tools with the community.</p>
            </div>
            <button
              onClick={() => navigate('/register')}
              className="px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-500 transition-colors whitespace-nowrap"
            >
              Get Started Free
            </button>
          </div>
        </div>
      )}

      {!isAuthenticated && <PublicFooter />}
    </div>
  )
}
